/**
 * 대시보드 순수 계산 로직 — 델타(증감률) 예외처리 + 밴드 0 알림 배너 아이템 계산.
 * React 렌더 밖에서 검증 가능하도록 순수 함수로 분리한다.
 */

import type { DrawerMetric } from './DetailDrawer'

/**
 * 증감 표시 계산 (0기준 예외처리).
 * - previous > 0: 기존대로 ▲/▼ N%.
 * - current > 0 && previous === 0: "신규" 배지 (▲100% 대신).
 * - current === 0 && previous === 0: 미표시(null).
 */
export function computeDelta(current: number, previous: number): { text: string; up: boolean } | null {
  if (previous === 0) {
    return current > 0 ? { text: '신규', up: true } : null
  }
  const pct = ((current - previous) / previous) * 100
  const up = pct >= 0
  return { text: `${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`, up }
}

export type BannerChip = {
  key: string
  label: string
  tone: 'warning' | 'danger'
  /** 클릭 시 여는 상세 Drawer metric — 없으면 비클릭 칩 */
  metric?: DrawerMetric
}

export interface BannerInput {
  openInquiries: number
  /** 환불 요청 건수 — 별도 fetch라 미도착 시 null */
  pendingRefunds: number | null
  aiFailRate24h: number
  expiringSoon: number
}

/** 밴드 0 알림 배너 — 노출 조건 충족 항목만 칩으로. 전부 미충족이면 null. */
export function computeBannerItems(
  input: BannerInput,
): { chips: BannerChip[]; tone: 'warning' | 'danger' } | null {
  const chips: BannerChip[] = []
  if (input.openInquiries > 0) {
    chips.push({ key: 'openInquiries', label: `미처리 문의 ${input.openInquiries}`, tone: 'warning', metric: 'openInquiries' })
  }
  if (input.pendingRefunds != null && input.pendingRefunds > 0) {
    chips.push({ key: 'refunds', label: `환불 요청 ${input.pendingRefunds}`, tone: 'danger' })
  }
  if (input.aiFailRate24h >= 10) {
    chips.push({ key: 'aiFailRate24h', label: `AI 실패율 ${input.aiFailRate24h.toFixed(1)}%`, tone: 'danger', metric: 'aiFailRate24h' })
  }
  if (input.expiringSoon > 0) {
    chips.push({ key: 'expiringSoon', label: `만료 임박 ${input.expiringSoon}`, tone: 'warning', metric: 'expiringSoon' })
  }
  if (chips.length === 0) return null
  const tone = chips.some((c) => c.tone === 'danger') ? 'danger' : 'warning'
  return { chips, tone }
}
